"use client";

import type { InputHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { cn } from "../../lib";
import { Input } from "../input";
import { buildControlDescribedBy, fieldMixedId } from "./field-a11y";
import { FieldCharCountBadge } from "./field-char-count-badge";
import {
	FieldShell,
	type FieldLayoutHint,
	type FieldLabelVariant,
} from "./field-shell";

export interface TextFieldProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> {
	id: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	type?: InputHTMLAttributes<HTMLInputElement>["type"];
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	layoutHint?: FieldLayoutHint;
	labelVariant?: FieldLabelVariant;
	labelTrailing?: ReactNode;
	hideLabel?: boolean;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
	/** When true with `maxLength`, shows inset-end `current/max` inside the control. */
	showCharCount?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
	function TextField(
		{
			id,
			label,
			value,
			onChange,
			description,
			error,
			reserveErrorSpace = false,
			layoutHint,
			labelVariant = "default",
			labelTrailing,
			hideLabel = false,
			mixed = false,
			mixedTooltip,
			mixedPlaceholder = "Mixed values",
			showCharCount = false,
			type = "text",
			className,
			maxLength,
			...inputProps
		},
		ref,
	) {
		const mixedId = fieldMixedId(id);
		const showMixedPlaceholder = mixed && !value.trim();
		const controlDescribedBy = buildControlDescribedBy({
			id,
			error,
			hasDescription: Boolean(description),
			mixedId: mixed ? mixedId : undefined,
		});
		const showCount =
			Boolean(showCharCount) && typeof maxLength === "number";

		return (
			<FieldShell
				id={id}
				label={hideLabel ? undefined : label}
				description={description}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				labelVariant={labelVariant}
				labelTrailing={labelTrailing}
				describedById={mixed ? mixedId : undefined}
				className={className}
			>
				{mixed && mixedTooltip ? (
					<div className="mb-1 text-xs text-muted-foreground">
						{mixedTooltip}
					</div>
				) : null}
				{showMixedPlaceholder ? (
					<p id={mixedId} className="mb-1 text-xs text-muted-foreground">
						{mixedPlaceholder}
					</p>
				) : mixed ? (
					<span id={mixedId} className="sr-only">
						{mixedPlaceholder}
					</span>
				) : null}
				<div className="relative">
					<Input
						ref={ref}
						id={id}
						type={type}
						value={showMixedPlaceholder ? "" : value}
						maxLength={maxLength}
						placeholder={
							showMixedPlaceholder
								? mixedPlaceholder
								: inputProps.placeholder
						}
						onChange={(event) => onChange(event.target.value)}
						aria-label={hideLabel ? label : undefined}
						aria-invalid={
							Boolean(error) || (mixed && !value.trim())
								? true
								: undefined
						}
						aria-describedby={controlDescribedBy}
						className={cn(showCount && "pr-14")}
						{...inputProps}
					/>
					{showCount ? (
						<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
							<FieldCharCountBadge
								id={`${id}-char-count`}
								current={value.length}
								max={maxLength}
							/>
						</div>
					) : null}
				</div>
			</FieldShell>
		);
	},
);
